'use client';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import Modal from '@/app/ui/components/filters/mobile/Modal';
import useModal from '@/app/helpers/hooks/useModal';
import {
  FilterOptionKeys,
  FilterOptions,
  FilterOptionValue,
  FilterSortOrder,
  ProductCounters,
  SearchParams,
} from '@mybestbrands/types';
// import { FilterKey, FiltersRecord, SearchParamsType } from '@/app/types/searchparams';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import styles from './style.module.scss';
import SubFilter from '@/app/ui/components/filters/mobile/SubFilter';
import { ChevronRight } from 'react-bootstrap-icons';
import { Divider } from '@/app/ui/components/filters/mobile/Divider';
import { SelectedFiltersContainerMobile } from '@/app/ui/components/filters/mobile/SelectedFiltersContainerMobile';
import { getCurrentLocalePath } from '@/app/helpers/locale';
import { SortFilterMobile } from '@/app/ui/components/filters/mobile/SortFilterMobile';
import { getSelectedFilterCount } from '@/app/utils/common';
import { trackSSTEvent } from '@/app/utils/tracking';

interface Props {
  isVisible: boolean;
  onClose: () => void;
  facets: FilterOptions | null;
  searchParams: SearchParams;
  counters: ProductCounters | null;
  disabledFilters?: FilterOptionKeys[];
  activeSubFilter?: string | null;
  // showCategories: () => void;
  isTopFilter?: boolean;
  blackFriday?: boolean | undefined;
}

const SUBFILTERS = ['sizes', 'brands', 'materials', 'patterns', 'retailers', 'colors', 'price'];

function SubFilterItem(props: { name: string; selectedCount: number; onSelectSubFilter: () => void }) {
  return (
    <div className={styles.filteroption} onClick={props.onSelectSubFilter}>
      <div>
        <span className="me-2">{props.name}</span>
        {props.selectedCount > 0 && <span className={styles.filterbadge}>{props.selectedCount}</span>}
      </div>
      <ChevronRight />
    </div>
  );
}

export function FiltersModal(props: Props) {
  const {
    isVisible,
    counters,
    onClose,
    facets,
    searchParams,
    disabledFilters,
    activeSubFilter,
    //showCategories,
    isTopFilter,
    blackFriday,
  } = props;
  const { isModalOpen, closeModal } = useModal(isVisible);
  const [activeSubFilters, setActiveSubFilters] = React.useState<FilterOptionKeys | string>('');
  const pathSearchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  // Keep track of modified filters to track changes
  const [modifiedFilters, setModifiedFilters] = useState<Map<string, number>>(new Map());

  //TODO dsantic 15.01.2024. We have to sort them by correct order!!!
  const subFilters = Object.entries(facets || {}).reduce((acc, [key, value]) => {
    if (SUBFILTERS.includes(key)) {
      acc[key as FilterOptionKeys] = value as FilterOptionValue;
    }
    return acc;
  }, {} as FilterOptions);

  const getFilterSortOrder = (key: FilterOptionKeys): number => {
    return FilterSortOrder[key.toUpperCase() as keyof typeof FilterSortOrder];
  };

  const sortedSubFilters = Object.entries(subFilters)
    .map(([key, value]) => ({ key, value }))
    .sort((a, b) => getFilterSortOrder(a.key as FilterOptionKeys) - getFilterSortOrder(b.key as FilterOptionKeys));

  const { t, i18n } = useTranslation(['filters', 'navigation']);
  const localePath = getCurrentLocalePath(i18n.language);

  useEffect(() => {
    if (isVisible) {
      if (activeSubFilter) {
        setActiveSubFilters(activeSubFilter);
      } else {
        setActiveSubFilters('');
      }
    }
  }, [isVisible, activeSubFilter]);

  function onCloseModal() {
    // Trigger the event only if it is coming from top filters
    if (isTopFilter) {
      // Loop through modified filters and trigger events
      modifiedFilters.forEach((count, filterKey) => {
        trackSSTEvent('top_filter_select', {
          filter_name: filterKey,
          selected_count: filterKey !== 'price' ? count : 1,
        });
      });
    }

    // Reset the state and close modal
    setModifiedFilters(new Map());
    onClose();
    closeModal();
    setActiveSubFilters('');
  }

  // function openCat() {
  //   showCategories();
  //   closeModal();
  // }

  function onApplySearchFilters(newSp?: SearchParams | null) {
    const filters = newSp?.filters ?? searchParams?.filters;
    const params = new URLSearchParams(pathSearchParams ?? '');
    const page = params.get('page');

    const selectedFilters = Object.entries(filters ?? {})
      .filter(([, value]) => {
        if (Array.isArray(value)) return value.length > 0;
        return !!value;
      })
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    //On each filter change we have to remove the page param
    //because we want to start from the first page
    if (page) params.delete('page');

    params.set('filters', JSON.stringify(selectedFilters));
    const url = `${pathname}?${params.toString()}`;

    router.push(url);
  }

  // Track filter selections and count the number of selected options
  function onSearchParamsUpdate(
    searchParamKey: FilterOptionKeys,
    value: string[] | number[] | number
  ): SearchParams | null {
    if (searchParams && searchParamKey in searchParams?.filters) {
      const newFilters = { ...searchParams, filters: { ...searchParams.filters, [searchParamKey]: value } };

      // Update the modified filters map with the count of selected items
      setModifiedFilters((prev) => {
        const updated = new Map(prev);
        updated.set(searchParamKey, Array.isArray(value) ? value.length : 1);
        return updated;
      });

      onApplySearchFilters(newFilters); // Apply the filter
    }
    return null;
  }

  return (
    <>
      {isModalOpen && (
        <Modal>
          {activeSubFilters && (
            <SubFilter
              onFilterUpdate={onSearchParamsUpdate}
              productCounters={counters?.totalNoProducts}
              hasSearch={[FilterOptionKeys.BRANDS, FilterOptionKeys.RETAILERS].includes(
                activeSubFilters as FilterOptionKeys
              )}
              onbackClick={() => setActiveSubFilters('')}
              filter={subFilters[activeSubFilters as FilterOptionKeys]}
              isTopFilter={isTopFilter}
              onCloseModal={onCloseModal}
              facets={facets}
              disabledFilters={disabledFilters}
              searchParams={searchParams}
              blackFriday={blackFriday}
            />
          )}
          {!activeSubFilters && (
            <div className="bg-white w-100 h-100 fixed-top overflow-auto">
              <div className="d-flex aligncenter justify-content-between mt-3 mx-4">
                <span className="aligncenter" style={{ fontSize: '1.25rem', fontWeight: 700 }}>
                  {t('FILTERS')}
                </span>
                <Image onClick={onCloseModal} src="/intstatic/icons/close.svg" alt="Close" height={14} width={14} />
              </div>
              <Divider className="mt-3" />
              <div className="mx-4">
                <div className="mb-4 mt-4">
                  <SelectedFiltersContainerMobile
                    localePath={localePath}
                    searchParams={searchParams}
                    facets={facets}
                    excludedFilters={[FilterOptionKeys.GENDERS]}
                  />
                  <SortFilterMobile filter={facets?.sorting} />
                </div>
                <Divider />
                <div className="mt-3" style={{ marginBottom: 100 }}>
                  {sortedSubFilters.map(({ key }) => (
                    <SubFilterItem
                      selectedCount={getSelectedFilterCount(facets, key, searchParams)}
                      onSelectSubFilter={() => setActiveSubFilters(key)}
                      key={key}
                      name={t(key)}
                    />
                  ))}
                  {/*<div className={styles.filteroption} onClick={openCat}>*/}
                  {/*  <div>*/}
                  {/*    <span className="me-2">Kategorien SIDEBAR</span>*/}
                  {/*  </div>*/}
                  {/*  <ChevronRight />*/}
                  {/*</div>*/}
                </div>
                <div className={styles.filterbuttonapply}>
                  <div className="row">
                    <div>
                      <button className="btn btn-black lowercase full-width" onClick={onCloseModal}>
                        {t('DONE')}
                        <span className="ms-1">({counters?.totalNoProducts ?? 0})</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal>
      )}
    </>
  );
}
